/**
 * Patrol scene blending aligned with Marzipano’s **default** scene transition:
 *
 * - Sample tour (`scene.switchTo()` with no options) calls `Viewer.switchScene`, which uses
 *   **linear** progress `t ∈ [0, 1]` over **`transitionDuration` = 1000 ms**, and applies
 *   **`opacity: t`** on the **incoming** scene layers only (outgoing stays fully visible underneath).
 * - Demo sources:
 *   - https://www.marzipano.net/demos/sample-tour/
 *   - Default behaviour: https://github.com/google/marzipano/blob/master/src/Viewer.js (`defaultTransitionUpdate`)
 * - Named transitions (opacity / slide / etc.): https://www.marzipano.net/demos/transitions/
 *
 * In **flat mode** / single-surface fallback we approximate that by dipping the patrol layer opacity
 * down then back up around the `<video>` `src` swap (never to zero → no solid black gap).
 *
 * In **Three.js patrol** `(viewMode fish|normal)`, we replicate the **real dissolve**: a second sphere
 * with a second decoded clip so outgoing opacity **1→0** and incoming **0→1** over
 * PATROL_SAMPLE_TOUR_TRANSITION_MS (same linear timing as Viewer.switchScene).
 */

/** Matches Marzipano `defaultSwitchDuration` (Viewer.switchScene). */
export const PATROL_SAMPLE_TOUR_TRANSITION_MS = 1000;

/**
 * Minimum opacity during the decode/swap window — analogous to brief overlap before the incoming
 * scene reaches full opacity; avoids a full black gap when only one surface is composited.
 */
export const PATROL_SAMPLE_TOUR_BLEND_FLOOR = 0.38;

/**
 * Phase 1 duration (ms): linear 1 → blend floor before swapping `video.src`.
 * Symmetric 50/50 split approximates equal weight to outgoing/incoming like a dual-layer fade.
 */
export const PATROL_SAMPLE_TOUR_DIP_MS = Math.round(
  PATROL_SAMPLE_TOUR_TRANSITION_MS * 0.5,
);

/** Phase 2: linear blend floor → 1 after `play()` on the new clip. */
export const PATROL_SAMPLE_TOUR_RESTORE_MS =
  PATROL_SAMPLE_TOUR_TRANSITION_MS - PATROL_SAMPLE_TOUR_DIP_MS;

/** Identity — Marzipano default `transitionUpdate` uses linear `t` (no easing). */
export function patrolLinear(linearT) {
  return Math.min(1, Math.max(0, linearT));
}

/** easing-js–style easeInQuad (optional; transitions demo presets). */
export function patrolEaseInQuad(linearT) {
  const t = Math.min(1, Math.max(0, linearT));
  return t * t;
}

/** easing-js–style easeOutQuad (optional; transitions demo presets). */
export function patrolEaseOutQuad(linearT) {
  const t = Math.min(1, Math.max(0, linearT));
  return 1 - (1 - t) * (1 - t);
}

/**
 * Opacity tween: opacity = from + (to - from) * ease(linearT).
 *
 * @param {HTMLElement | null} el
 * @param {number} fromOpacity
 * @param {number} toOpacity
 * @param {number} durationMs
 * @param {(linearT: number) => number} ease
 * @returns {Promise<void>}
 */
export function animatePatrolLayerOpacity(
  el,
  fromOpacity,
  toOpacity,
  durationMs,
  ease,
) {
  return new Promise((resolve) => {
    if (!el) {
      resolve();
      return;
    }

    const start = performance.now();
    const delta = toOpacity - fromOpacity;

    function frame(now) {
      const linearT = Math.min(1, (now - start) / durationMs);
      const eased = ease(linearT);
      const opacity = fromOpacity + delta * eased;
      el.style.opacity = String(opacity);

      if (linearT < 1) {
        requestAnimationFrame(frame);
      } else {
        el.style.opacity = String(toOpacity);
        resolve();
      }
    }

    el.style.opacity = String(fromOpacity);
    requestAnimationFrame(frame);
  });
}

/** Phase 1: 1 → blend floor with linear easing (sample-tour default = linear `t`). */
export function patrolSampleTourBlendDown(
  el,
  durationMs = PATROL_SAMPLE_TOUR_DIP_MS,
) {
  return animatePatrolLayerOpacity(
    el,
    1,
    PATROL_SAMPLE_TOUR_BLEND_FLOOR,
    durationMs,
    patrolLinear,
  );
}

/** Phase 2: blend floor → 1 with linear easing. */
export function patrolSampleTourBlendUp(
  el,
  durationMs = PATROL_SAMPLE_TOUR_RESTORE_MS,
) {
  return animatePatrolLayerOpacity(
    el,
    PATROL_SAMPLE_TOUR_BLEND_FLOOR,
    1,
    durationMs,
    patrolLinear,
  );
}

/**
 * Dual-layer dissolve: outgoing opacity **1→0**, incoming **0→1**, linear — matches the Recording /
 * sample-tour overlap (both panoramas visible mid-blend).
 *
 * @param {{ opacity: number }} materialOutgoing Primary sphere (currently playing clip).
 * @param {{ opacity: number }} materialIncoming Crossfade sphere (decoded next clip).
 * @param {number} [durationMs]
 * @returns {Promise<void>}
 */
export function animatePatrolMarzipanoSphereCrossfade(
  materialOutgoing,
  materialIncoming,
  durationMs = PATROL_SAMPLE_TOUR_TRANSITION_MS,
) {
  return new Promise((resolve) => {
    if (!materialOutgoing || !materialIncoming) {
      resolve();
      return;
    }

    const start = performance.now();

    function frame(now) {
      const linearT = Math.min(1, (now - start) / durationMs);
      const t = patrolLinear(linearT);
      materialOutgoing.opacity = 1 - t;
      materialIncoming.opacity = t;

      if (linearT < 1) {
        requestAnimationFrame(frame);
      } else {
        materialOutgoing.opacity = 0;
        materialIncoming.opacity = 1;
        resolve();
      }
    }

    materialOutgoing.opacity = 1;
    materialIncoming.opacity = 0;
    requestAnimationFrame(frame);
  });
}
